import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { FaHome, FaEnvelope, FaUser, FaGlobe, FaCog, FaBars, FaTimes, FaChevronDown, FaSearch, FaWallet, FaTrophy } from 'react-icons/fa';
import styles from '../styles/Header.module.css';

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Page configuration
  const pageConfig = {
    '/Home': { title: 'Home', icon: <FaHome /> },
    '/e_card': { title: 'E-Card', icon: <FaEnvelope /> },
    '/WatoaHuduma': { title: 'Watoa Huduma', icon: <FaSearch /> },
    '/About': { title: 'About', icon: <FaGlobe /> },
    '/Contact': { title: 'Contact', icon: <FaUser /> },
    '/Gharama': { title: 'Gharama', icon: <FaWallet /> },
    '/terms': { title: 'Terms & Conditions', icon: <FaGlobe /> },
    '/privacy': { title: 'Privacy Policy', icon: <FaGlobe /> },
    '/privacy-policy': { title: 'Privacy Policy', icon: <FaGlobe /> },
    '/awards': { title: 'Awards', icon: <FaTrophy /> },
    '/Awards': { title: 'Awards', icon: <FaTrophy /> },
  };

  // Navigation items
  const navItems = [
    { path: '/Home', label: 'Home', icon: <FaHome /> },
    { path: '/e_card', label: 'E-Card', icon: <FaEnvelope /> },
    { path: '/WatoaHuduma', label: 'Watoa Huduma', icon: <FaSearch /> },
    { path: '/Gharama', label: 'Gharama', icon: <FaWallet /> },
  ];

  // Get current page info
  const currentPath = location.pathname;
  const currentPage = pageConfig[currentPath] || { title: 'Harusi Online', icon: <FaGlobe /> };

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      {/* Desktop Header */}
      <header className={`${styles.customHeader} ${scrolled ? styles.scrolled : ''}`}>
        <Container className={styles.headerContainer}>
          <div className={styles.headerContent}>
            {/* Logo Section */}
            <div className={styles.logoSection}>
              <Link to="/Home" className={styles.logo}>
                <div className={styles.logoIcon}>
                  <img
                    src="/logo1.png"
                    alt="Harusi Online Logo"
                    className={styles.logoImg}
                  />
                </div>
                <div className={styles.logoText}>
                  <span className={styles.logoMain}>Harusi Online</span>
                  <span className={styles.logoSubtitle}>Your Wedding Solution</span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className={`${styles.desktopNav}`}>
              <ul className={styles.navList}>
                {navItems.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ''}`
                      }
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}

                {/* More Options Dropdown */}
                <li className={styles.navDropdownItem}>
                  <div className={styles.customDropdown}>
                    <button className={styles.dropdownToggle}>
                      <FaGlobe />
                      <span>More Options</span>
                      <FaChevronDown className={styles.dropdownArrow} />
                    </button>
                    <div className={styles.dropdownMenu}>
                      <NavLink to="/About" className={styles.dropdownItem}>
                        About
                      </NavLink>
                      <NavLink to="/Contact" className={styles.dropdownItem}>
                        Contact
                      </NavLink>
                      <NavLink to="/terms" className={styles.dropdownItem}>
                        Terms & Conditions
                      </NavLink>
                      <NavLink to="/privacy" className={styles.dropdownItem}>
                        Privacy Policy
                      </NavLink>
                      <div className={styles.dropdownDivider}></div>
                      <NavLink to="/awards" className={styles.dropdownItem}>
                        <FaTrophy className="me-2" />
                        Awards
                      </NavLink>
                    </div>
                  </div>
                </li>
              </ul>
            </nav>

            {/* Login/Register Button - Outside navigation */}
            <div className={styles.adminButton}>
              <Link to="/login" className={styles.btnAdmin}>
                <FaUser />
                <span>Jisajili / Ingia</span>
              </Link>
            </div>
          </div>
        </Container>
      </header>

      {/* Mobile Header */}
      <div className={`${styles.mobileHeader}`}>
        <Container className={styles.headerContainer}>
          <div className={styles.mobileTopBar}>
            <div className={styles.mobileLogo}>
              <div className={styles.mobileLogoIcon}>
                <img
                  src="/logo1.png"
                  alt="Harusi Online Logo"
                  className={styles.mobileLogoImg}
                />
              </div>
              <div className={styles.mobilePageTitle}>
                {currentPage.icon}
                <span>{currentPage.title}</span>
              </div>
            </div>

            <button
              className={styles.mobileMenuBtn}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </Container>
      </div>

      {/* Mobile Navigation Menu Overlay */}
      <div
        className={`${styles.mobileOverlay} ${mobileMenuOpen ? styles.active : ''}`}
        onClick={toggleMobileMenu}
      />

      {/* Mobile Navigation Menu */}
      <div className={`${styles.mobileNav} ${mobileMenuOpen ? styles.active : ''}`}>
        <div className={styles.mobileNavHeader}>
          <div className={styles.mobileNavLogo}>
            <div className={styles.mobileNavLogoIcon}>
              <img
                src="/logo1.png"
                alt="Harusi Online Logo"
                className={styles.mobileNavLogoImg}
              />
            </div>
            <h2>Harusi Online</h2>
          </div>
          <button
            className={styles.closeMenu}
            onClick={toggleMobileMenu}
            aria-label="Close mobile menu"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.mobileNavLinks}>
          {[...navItems,
          { path: '/About', label: 'About', icon: <FaGlobe /> },
          { path: '/Contact', label: 'Contact', icon: <FaUser /> },
          { path: '/terms', label: 'Terms & Conditions', icon: <FaGlobe /> },
          { path: '/privacy', label: 'Privacy Policy', icon: <FaGlobe /> },
          { path: '/settings', label: 'Settings', icon: <FaCog /> }
          ].map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `${styles.mobileNavLink} ${isActive ? styles.active : ''}`
              }
              onClick={toggleMobileMenu}
              style={{ animationDelay: `${0.1 + index * 0.05}s` }}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className={styles.mobileAdminSection}>
          <Link to="/login" className={styles.mobileAdminBtn} onClick={toggleMobileMenu}>
            <FaUser />
            <span>Jisajili / Ingia</span>
          </Link>
        </div>
      </div>
    </>
  );
}

export default Header;